"use strict";
/**
 * Structured persistence for the "Brand Status Quo & Objective" onboarding
 * questionnaire. Everything here is derived from the single
 * question source of truth (`BRAND_STATUS_QUO_QUESTIONS`) so the persisted shape,
 * the capture endpoint's body, and the LLM backfill schema can never drift from it.
 *
 * Per question we distinguish three states:
 *  - answered           → `{ selectedOptionIds, other? }`
 *  - shown but skipped  → `{ skipped: true }`
 *  - not shown / N/A     → the question key is ABSENT from `answers`
 *
 * Option IDs are canonical and stored verbatim (never labels), so rewording a
 * question or option is a no-op for stored data. Special options carry meaning via
 * their id alone (`channels: 'none'` = not running paid ads, `focus: 'all'` = everything).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BRAND_STATUS_QUO_RESPONSE_BODY_SCHEMA = exports.BRAND_STATUS_QUO_OTHER_OPTION_ID = exports.BRAND_STATUS_QUO_QUESTION_SET_VERSION = void 0;
exports.buildBrandStatusQuoResponseBody = buildBrandStatusQuoResponseBody;
exports.normalizeStatusQuoBody = normalizeStatusQuoBody;
exports.buildBrandStatusQuoExtractionSchema = buildBrandStatusQuoExtractionSchema;
exports.extractionResultToPayload = extractionResultToPayload;
exports.resolveBrandStatusQuoAnswerLabels = resolveBrandStatusQuoAnswerLabels;
const zod_1 = require("zod");
const brand_status_quo_onboarding_1 = require("./brand-status-quo-onboarding");
const brand_status_quo_onboarding_v2_1 = require("./brand-status-quo-onboarding-v2");
const question_attachment_reference_1 = require("../utils/chat/question-attachment-reference");
/**
 * Bump on any SEMANTIC change to the question set (add/remove/restructure a question
 * or option, flip single↔multi, change a `showIf`). Leave it for cosmetic label/caption
 * edits. Stored on every row so consumers and migrations know which set it used.
 */
exports.BRAND_STATUS_QUO_QUESTION_SET_VERSION = 1;
/** Frontend sentinel for the free-text "Other"/"Something else" choice; never persisted as an option id. */
exports.BRAND_STATUS_QUO_OTHER_OPTION_ID = '__other__';
/**
 * POST body for the live-capture endpoint. Deliberately permissive (no option-id enum)
 * so a stale client after a future question-set bump is recorded rather than rejected;
 * ids are validated/cleaned against the question set in `normalizeStatusQuoBody`.
 */
exports.BRAND_STATUS_QUO_RESPONSE_BODY_SCHEMA = zod_1.z.object({
    version: zod_1.z.number().int().nonnegative(),
    /** Ids of the questions actually shown to the user (the source of truth for not-shown = absent). */
    visibleQuestionIds: zod_1.z.array(zod_1.z.string()),
    answers: zod_1.z.record(zod_1.z.string(), zod_1.z.object({
        selectedOptionIds: zod_1.z.array(zod_1.z.string()),
        other: zod_1.z.string().optional(),
        attachments: zod_1.z
            .array(zod_1.z.object({
            type: zod_1.z.string(),
            uuid: zod_1.z.string(),
        }))
            .optional(),
        skipped: zod_1.z.boolean().optional(),
    })),
});
/**
 * Build the capture body from the wizard's raw option-id state. Kept here so the
 * `'__other__'`-stripping and skip logic live in one place (shared by the renderer).
 */
function buildBrandStatusQuoResponseBody(visibleQuestions, rawAnswers) {
    const answers = {};
    for (const q of visibleQuestions) {
        const raw = rawAnswers[q.id];
        const selectedOptionIds = (raw?.selected ?? []).filter((id) => id !== exports.BRAND_STATUS_QUO_OTHER_OPTION_ID);
        const other = (raw?.selected?.includes(exports.BRAND_STATUS_QUO_OTHER_OPTION_ID) ? raw.other : '').trim();
        const customAnswerIsActive = raw?.selected?.includes(exports.BRAND_STATUS_QUO_OTHER_OPTION_ID) ?? false;
        const attachments = customAnswerIsActive
            ? (0, question_attachment_reference_1.dedupeQuestionAttachmentReferences)(raw?.attachments ?? [])
            : [];
        const skipped = selectedOptionIds.length === 0 && other.length === 0 && attachments.length === 0;
        answers[q.id] = {
            selectedOptionIds,
            ...(other ? { other } : {}),
            ...(attachments.length > 0 ? { attachments } : {}),
            skipped,
        };
    }
    return {
        version: exports.BRAND_STATUS_QUO_QUESTION_SET_VERSION,
        visibleQuestionIds: visibleQuestions.map((q) => q.id),
        answers,
    };
}
/**
 * Build a non-skipped answer. A custom `other` answer is marked explicitly by appending the
 * `'__other__'` sentinel to `selectedOptionIds`, so a pure-"Other" answer never looks empty to
 * structured reads (invariant: a non-skipped answer always has a non-empty `selectedOptionIds`).
 */
function buildAnswer(realOptionIds, other, attachments = []) {
    const hasCustomAnswer = Boolean(other) || attachments.length > 0;
    const selectedOptionIds = hasCustomAnswer
        ? [...realOptionIds, exports.BRAND_STATUS_QUO_OTHER_OPTION_ID]
        : realOptionIds;
    return {
        selectedOptionIds,
        ...(other ? { other } : {}),
        ...(attachments.length > 0 ? { attachments } : {}),
    };
}
/**
 * Normalize a validated capture body into the canonical persisted payload:
 * strip the `'__other__'` sentinel + unknown ids, gate free-text on `allowOther`,
 * and OMIT every question not in `visibleQuestionIds` (encodes not-shown / N/A).
 *
 * Ids are validated against the question set the CLIENT says it rendered (`body.version`), not
 * against v1 unconditionally — otherwise a v2 body would be stored stamped `version: 2` while
 * every one of its answers had been filtered against v1's ids (i.e. silently emptied). Unknown
 * versions still fall back to v1, keeping the permissive stale-client policy.
 */
function normalizeStatusQuoBody(body) {
    const visible = new Set(body.visibleQuestionIds);
    const answers = {};
    for (const question of (0, brand_status_quo_onboarding_v2_1.questionSetForVersion)(body.version)) {
        if (!visible.has(question.id))
            continue;
        const raw = body.answers[question.id];
        const validOptionIds = new Set(question.options.map((o) => o.id));
        const selectedOptionIds = (raw?.selectedOptionIds ?? [])
            .filter((id) => id !== exports.BRAND_STATUS_QUO_OTHER_OPTION_ID)
            .filter((id) => validOptionIds.has(id));
        const other = question.allowOther ? (raw?.other ?? '').trim() : '';
        const attachments = question.allowOther
            ? (0, question_attachment_reference_1.dedupeQuestionAttachmentReferences)((raw?.attachments ?? []).filter((reference) => (0, question_attachment_reference_1.isQuestionAttachmentReference)(reference) && zod_1.z.string().uuid().safeParse(reference.uuid).success))
            : [];
        const skipped = selectedOptionIds.length === 0 && other.length === 0 && attachments.length === 0;
        answers[question.id] = skipped
            ? { selectedOptionIds: [], skipped: true }
            : buildAnswer(selectedOptionIds, other, attachments);
    }
    return { version: body.version, answers };
}
/**
 * Zod schema for the backfill LLM extraction, derived from the question set: a field
 * per question whose enum values are the exact option ids (array for multi-select),
 * plus a `<qid>_other` free-text field for `allowOther` questions. `.nullable()` lets
 * the model say "not stated in the report".
 *
 * Deliberately v1-locked: backfill reconstructs answers from prose reports written by the
 * chat-based onboarding flow, which only ever asked v1's questions.
 */
function buildBrandStatusQuoExtractionSchema() {
    const shape = {};
    for (const question of brand_status_quo_onboarding_1.BRAND_STATUS_QUO_QUESTIONS) {
        const optionIds = question.options.map((o) => o.id);
        const base = `${question.title} Option ids: ${optionIds.join(', ')}.`;
        if (question.select === 'multi') {
            shape[question.id] = zod_1.z
                .array(zod_1.z.enum(optionIds))
                .nullable()
                .describe(`${base} Include every id the report states; null if not stated.`);
        }
        else {
            shape[question.id] = zod_1.z.enum(optionIds).nullable().describe(`${base} null if not stated.`);
        }
        if (question.allowOther) {
            shape[`${question.id}_other`] = zod_1.z
                .string()
                .nullable()
                .describe(`Free-text for "${question.id}" the report implies but no option covers; else null.`);
        }
    }
    return zod_1.z.object(shape);
}
/**
 * Map a backfill extraction result into the canonical payload. Applies conditional
 * applicability (`showIf`): a question whose dependency isn't satisfied is OMITTED
 * (not-applicable), matching live capture. Unstated questions are omitted too — a
 * prose report can't distinguish "shown but skipped" from "never asked", so backfill
 * never emits `skipped`.
 *
 * v1-locked for the same reason as the schema above — it consumes that schema's result and
 * therefore always emits a v1-stamped payload.
 */
function extractionResultToPayload(result) {
    const answers = {};
    for (const question of brand_status_quo_onboarding_1.BRAND_STATUS_QUO_QUESTIONS) {
        if (question.showIf) {
            const dep = result[question.showIf.questionId];
            const depSelected = Array.isArray(dep) ? dep : dep != null ? [dep] : [];
            const applies = depSelected.some((v) => question.showIf.optionIds.includes(v));
            if (!applies)
                continue;
        }
        const raw = result[question.id];
        const validOptionIds = new Set(question.options.map((o) => o.id));
        const selectedOptionIds = (Array.isArray(raw) ? raw : raw != null ? [raw] : []).filter((v) => typeof v === 'string' && validOptionIds.has(v));
        const otherRaw = question.allowOther ? result[`${question.id}_other`] : null;
        const other = typeof otherRaw === 'string' ? otherRaw.trim() : '';
        if (selectedOptionIds.length === 0 && !other)
            continue;
        answers[question.id] = buildAnswer(selectedOptionIds, other);
    }
    return { version: exports.BRAND_STATUS_QUO_QUESTION_SET_VERSION, answers };
}
/**
 * Resolve a persisted payload to display labels, in question order — the single mapping from
 * stored option ids to human labels (e.g. `spend:'s31_100'` → "$31K–$100K"). Only questions
 * present in the payload are returned (answered or skipped); not-shown ones stay omitted.
 *
 * Resolution keys off the payload's OWN `version`, so a v2 row resolves against v2 wording and a
 * v1 row keeps resolving against v1 — ids like `spend:'s1_10'` exist in both sets with different
 * labels, so reading either with the wrong set would silently mislabel it.
 */
function resolveBrandStatusQuoAnswerLabels(payload) {
    const resolved = [];
    for (const question of (0, brand_status_quo_onboarding_v2_1.questionSetForVersion)(payload.version)) {
        const answer = payload.answers[question.id];
        if (!answer)
            continue;
        const labels = answer.selectedOptionIds
            .filter((id) => id !== exports.BRAND_STATUS_QUO_OTHER_OPTION_ID)
            .map((id) => question.options.find((o) => o.id === id)?.label)
            .filter((label) => !!label);
        resolved.push({
            questionId: question.id,
            title: question.title,
            labels,
            ...(answer.other ? { other: answer.other } : {}),
            ...(answer.attachments?.length ? { attachments: answer.attachments } : {}),
            ...(answer.skipped ? { skipped: true } : {}),
        });
    }
    return resolved;
}
